import { useEffect, useState } from "react";
import { endpoints, fmtUSD, fmtTime } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { ArrowUpRight, Zap, ShieldAlert, Layers, Activity } from "lucide-react";
import { Link } from "react-router-dom";

export default function Overview() {
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    endpoints.summary().then(setSummary).catch(() => {});
    endpoints.runs().then((r) => setRuns(r.slice(0, 6))).catch(() => {});
    endpoints.offers().then((r) => setOffers(r.slice(0, 5))).catch(() => {});
  }, []);

  const kpis = [
    {
      label: "Total contract value",
      value: fmtUSD(summary?.total_tcv_usd),
      accent: "text-orange-600",
      icon: Layers,
    },
    {
      label: "Published authorizations",
      value: summary?.published_authorizations ?? 0,
      icon: ShieldAlert,
    },
    {
      label: "Awaiting approval",
      value: summary?.awaiting_approval ?? 0,
      accent: (summary?.awaiting_approval ?? 0) > 0 ? "text-amber-600" : undefined,
      icon: Zap,
    },
    {
      label: "Agent runs",
      value: summary?.run_count ?? 0,
      icon: Activity,
    },
  ];

  return (
    <div className="px-12 py-14 max-w-[1400px]" data-testid="page-overview">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tx-overline mb-2">Overview</div>
          <h1 className="font-heading font-black text-4xl">
            The supply-side engine for AWS Private Offers.
          </h1>
          <p className="text-zinc-600 mt-3 max-w-2xl text-[15px]">
            Design, validate & release CPPOs before the irreversible AWS Selling
            Authorization gate. All data flows through Salesforce, AWS ACE,
            AMMP and MCP agents — orchestrated by Claude Sonnet 5.
          </p>
        </div>
        <Link
          to="/agent"
          data-testid="cta-run-agent"
          className="hidden md:inline-flex items-center gap-2 h-10 px-4 bg-zinc-900 text-white text-[13px] font-medium hover:bg-zinc-700 transition-colors rounded-sm"
        >
          Run autonomous agent
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-zinc-200 bg-zinc-100 mb-10">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div
              key={k.label}
              className={`p-6 ${i < kpis.length - 1 ? "md:border-r border-zinc-200" : ""} border-b md:border-b-0 border-zinc-200`}
              data-testid={`kpi-${i}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="tx-overline">{k.label}</div>
                <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
              </div>
              <div
                className={`font-heading font-black text-4xl ${k.accent || "text-zinc-900"}`}
              >
                {k.value}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <section className="lg:col-span-3 border border-zinc-200 bg-zinc-50">
          <header className="h-11 px-5 border-b border-zinc-200 flex items-center justify-between">
            <div className="tx-overline">Recent agent runs</div>
            <Link
              to="/agent"
              className="text-[11px] text-zinc-600 hover:text-zinc-900 font-mono"
            >
              view all →
            </Link>
          </header>
          <div className="divide-y divide-zinc-200">
            {runs.length === 0 && (
              <div className="p-8 text-zinc-500 text-sm">
                No runs yet. Start one from the{" "}
                <Link className="text-orange-600 underline" to="/agent">
                  Agent Console
                </Link>
                .
              </div>
            )}
            {runs.map((r) => (
              <div key={r.id} className="px-5 py-4 flex items-center gap-4">
                <div className="font-mono text-[11px] text-zinc-500 w-28 shrink-0">
                  {fmtTime(r.created_at)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-zinc-900 truncate">
                    {r.intent}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {r.events?.length || 0} events
                  </div>
                </div>
                <StateBadge state={r.status} />
              </div>
            ))}
          </div>
        </section>

        <section className="lg:col-span-2 border border-zinc-200 bg-zinc-50">
          <header className="h-11 px-5 border-b border-zinc-200 flex items-center justify-between">
            <div className="tx-overline">CPPO pipeline</div>
            <Link
              to="/offers"
              className="text-[11px] text-zinc-600 hover:text-zinc-900 font-mono"
            >
              view all →
            </Link>
          </header>
          <div className="divide-y divide-zinc-200">
            {offers.length === 0 && (
              <div className="p-8 text-zinc-500 text-sm">
                No CPPOs yet.
              </div>
            )}
            {offers.map((o) => (
              <div key={o.id} className="px-5 py-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[11px] text-zinc-500">
                    {o.id}
                  </span>
                  <StateBadge state={o.state} />
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-[13px] text-zinc-800 truncate">
                    {o.reseller}
                  </div>
                  <div className="font-heading font-medium text-zinc-900">
                    {fmtUSD(o.total_contract_value_usd)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
